/**
 * Mobile-side affiliate provider singleton. Reads the TravelPayouts marker
 * from `Constants.expoConfig.extra` (populated by `apps/mobile/app.config.js`
 * from `EXPO_PUBLIC_TRAVELPAYOUTS_*` env). When the marker is unset the
 * factory returns `NoopAffiliateProvider` and every UI surface should
 * branch on `isAffiliatesEnabled()` to hide itself. See ADR-0064.
 */

import Constants from 'expo-constants';
import {
  type AffiliateEnv,
  type AffiliateProvider,
  createAffiliateProvider,
} from '@my-fullstack-app/affiliates';
import { useConsentStore } from '../../store/consent-store';

let cachedProvider: AffiliateProvider | null = null;

function readEnv(): AffiliateEnv {
  const extra = Constants.expoConfig?.extra as Record<string, unknown> | undefined;
  const marker = extra?.['travelpayoutsMarker'];
  const trs = extra?.['travelpayoutsTrs'];
  const host = extra?.['travelpayoutsHost'];
  const env: AffiliateEnv = {};
  if (typeof marker === 'string' && marker.length > 0) env.travelpayoutsMarker = marker;
  if (typeof trs === 'string' && trs.length > 0) env.travelpayoutsTrs = trs;
  if (typeof host === 'string' && host.length > 0) env.travelpayoutsHost = host;
  return env;
}

export function getAffiliateProvider(): AffiliateProvider {
  cachedProvider ??= createAffiliateProvider(readEnv());
  return cachedProvider;
}

/**
 * True when affiliate CTAs may render. ANDs provider-availability with
 * the user's affiliate-consent decision (ADR-0065) so a click never
 * lands on a partner domain without an explicit opt-in.
 *
 * Non-reactive — reads the consent store via `getState()`. Use the
 * `useAffiliatesEnabled` hook inside React components so the render
 * re-runs when consent flips mid-session (e.g. a user toggles
 * affiliates off via `/profile` → "Privacy & Tracking").
 */
export function isAffiliatesEnabled(): boolean {
  if (getAffiliateProvider().id === 'noop') return false;
  return useConsentStore.getState().consent?.affiliates === true;
}

/**
 * Reactive variant of `isAffiliatesEnabled` — subscribes to the
 * consent store so the calling component re-renders when
 * `consent.affiliates` flips. Required for any render-time gating
 * (CTA visibility on trip / stay / journey screens); the plain
 * function only gives the value at call time and won't trigger a
 * re-render when consent changes from another surface.
 */
export function useAffiliatesEnabled(): boolean {
  const consented = useConsentStore((s) => s.consent?.affiliates === true);
  return consented && getAffiliateProvider().id !== 'noop';
}

/** Test-only seam — mirrors `_setAnalyticsForTests`. */
export function _setAffiliateProviderForTests(next: AffiliateProvider | null): void {
  cachedProvider = next;
}

/** Test-only reset — mirrors `_resetAnalyticsForTests`. Forces the next
 *  `getAffiliateProvider()` call to re-read env. */
export function _resetAffiliateProviderForTests(): void {
  cachedProvider = null;
}
